import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Device Authorization pages for /authorize?user_code=XXXX-XXXX: the human half of
// the RFC-8628-style flow. An agent (Hermes, Claude Code, Codex) starts the flow and
// shows the user this link; the user signs in on the regular login page (GoogleLogin
// adds the connect-your-agent explanation on /authorize) and then allows the agent
// here (rendered inside App.js's `if (user)` block; the httpOnly cookie authenticates
// these calls).
// The copy is kept short and plain for people who have never heard of OAuth: what
// is happening, what the agent can do, and what to do next. See API.md.

const BASE = '/api/auth/device';

function Shell({ children }) {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.brand}>Hermes PM</div>
        {children}
      </div>
    </div>
  );
}

function fmtLeft(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function ApproveDevice() {
  const { getAuthHeader, user } = useAuth();

  const initialCode = (new URLSearchParams(window.location.search).get('user_code') || '').toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [phase, setPhase] = useState(initialCode ? 'loading' : 'enter'); // enter | loading | confirm | approved | denied | error
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  const hdrs = () => ({ Authorization: getAuthHeader(), 'Content-Type': 'application/json' });

  const loadInfo = useCallback(async (c) => {
    setPhase('loading');
    setMessage('');
    try {
      const res = await fetch(`${BASE}?user_code=${encodeURIComponent(c)}`, {
        headers: hdrs(),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhase('error');
        setMessage('This link has expired or was already used. Ask your agent to connect again and open the new link.');
        return;
      }
      if (data.status === 'approved') {
        setPhase('approved');
        return;
      }
      if (data.status === 'denied') {
        setPhase('denied');
        return;
      }
      setAgentName(data.clientName || '');
      setExpiresAt(data.expiresAt || 0);
      setPhase('confirm');
    } catch (e) {
      setPhase('error');
      setMessage('Could not reach Hermes PM. Check your connection and reload this page.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialCode) loadInfo(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown for the pending request (the code lives 10 minutes).
  useEffect(() => {
    if (phase !== 'confirm' || !expiresAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phase, expiresAt]);
  const expired = phase === 'confirm' && expiresAt && now >= expiresAt;

  const approve = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/approve`, {
        method: 'POST',
        headers: hdrs(),
        credentials: 'include',
        body: JSON.stringify({ user_code: code }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setPhase('approved');
      } else if (res.status === 403) {
        setPhase('error');
        setMessage(`${user?.email || 'This account'} isn't set up in Hermes PM yet. Ask your admin to add you, then ask your agent to connect again.`);
      } else {
        setPhase('error');
        setMessage('This link has expired or was already used. Ask your agent to connect again and open the new link.');
      }
    } catch {
      setPhase('error');
      setMessage('Could not reach Hermes PM. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const deny = async () => {
    setBusy(true);
    try {
      await fetch(`${BASE}/deny`, {
        method: 'POST',
        headers: hdrs(),
        credentials: 'include',
        body: JSON.stringify({ user_code: code }),
      });
      setPhase('denied');
    } catch {
      setPhase('error');
      setMessage('Could not reach Hermes PM. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c) { setCode(c); loadInfo(c); }
  };

  if (phase === 'approved') {
    return (
      <Shell>
        <h1 style={S.h1}>✓ You're connected</h1>
        <p style={S.p}>
          Go back to your chat and tell your agent <strong>"done"</strong>. It will finish connecting
          and offer a few optional settings.
        </p>
        <p style={S.muted}>You can close this page.</p>
      </Shell>
    );
  }

  if (phase === 'denied') {
    return (
      <Shell>
        <h1 style={S.h1}>Not connected</h1>
        <p style={S.p}>Your agent was not connected. Changed your mind? Ask your agent to connect to Hermes PM again.</p>
        <p style={S.muted}>You can close this page.</p>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <h1 style={S.h1}>Something went wrong</h1>
        <p style={S.err}>{message}</p>
      </Shell>
    );
  }

  if (phase === 'enter') {
    return (
      <Shell>
        <h1 style={S.h1}>This link is incomplete</h1>
        <p style={S.p}>
          Ask your agent to connect to Hermes PM again and open the link it gives you.
          If it showed you a code instead, enter it here.
        </p>
        <form onSubmit={submitCode}>
          <input
            style={S.input}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX"
            autoFocus
          />
          <button style={{ ...S.primary, width: '100%' }} type="submit" disabled={!code.trim()}>Continue</button>
        </form>
      </Shell>
    );
  }

  if (phase === 'loading') {
    return <Shell><p style={S.p}>Loading…</p></Shell>;
  }

  // confirm
  return (
    <Shell>
      <h1 style={S.h1}>Allow your agent to use Hermes PM?</h1>
      <p style={S.p}>
        You're signed in as <strong>{user?.email || 'you'}</strong>. Your agent
        {agentName ? <> ({agentName})</> : ''} will use Hermes PM as you:
      </p>
      <ul style={S.ul}>
        <li style={S.li}>It can show your to-dos and add or update them when you ask.</li>
        <li style={S.li}>It asks before changing anything, and gets no access to your Google email, files or calendar.</li>
      </ul>
      <p style={S.p}>If you didn't ask your agent to connect to Hermes PM, just ignore this and close the page.</p>

      {/* Allow on the right: the primary action sits where most people reach first. */}
      <div style={S.row}>
        <button style={S.secondary} onClick={deny} disabled={busy}>Cancel</button>
        <button style={S.primary} onClick={approve} disabled={busy || expired}>
          {busy ? 'Connecting…' : 'Allow'}
        </button>
      </div>

      <p style={S.muted}>
        {expired
          ? 'This request expired. Ask your agent to connect again.'
          : expiresAt ? `Expires in ${fmtLeft(expiresAt - now)}. ` : ''}
        {!expired && 'To disconnect later, ask your agent to reset Hermes PM.'}
      </p>
    </Shell>
  );
}

const S = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', padding: 16, boxSizing: 'border-box' },
  card: { width: '100%', maxWidth: 460, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '28px 24px', color: '#111827', boxSizing: 'border-box' },
  brand: { fontSize: 13, letterSpacing: '.04em', color: '#6b7280', marginBottom: 14, textTransform: 'uppercase' },
  h1: { fontSize: 22, margin: '0 0 12px', fontWeight: 600, lineHeight: 1.3 },
  p: { fontSize: 15, lineHeight: 1.55, margin: '0 0 12px', color: '#1f2937' },
  ul: { margin: '0 0 14px', paddingLeft: 20 },
  li: { fontSize: 15, lineHeight: 1.55, margin: '4px 0', color: '#1f2937' },
  muted: { fontSize: 13, lineHeight: 1.55, color: '#6b7280', margin: '16px 0 0' },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 18, letterSpacing: '.15em', textAlign: 'center', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: 8, color: '#111827', marginBottom: 16 },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  primary: { flex: 1, padding: '12px 16px', fontSize: 15, fontWeight: 600, background: '#2457d6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
  secondary: { flex: 1, padding: '12px 16px', fontSize: 15, fontWeight: 600, background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' },
  err: { fontSize: 15, lineHeight: 1.55, color: '#b42318', margin: 0 },
};
